import Chart from './Chart';
import * as d3 from 'd3';
import Tooltip from './Tooltip';

export default class Bar extends Chart {
  constructor(svg, height, width, margin, trnsTime, tooltipDiv) {
    super(svg, height, width, margin, trnsTime);

    this.tooltip = new Tooltip(tooltipDiv);
    this._initializeHelperFunctions();
    this._initializeChart();
  }

  _initializeHelperFunctions() {
    this.xPosition = d3.scaleLinear()
      .domain([0, 1])
      .range([0, this.width - this.margin.left - this.margin.right])

    this.yPosition = d3.scaleBand()
      .domain([0, 1 , 2])
      .range([0, this.height - this.margin.top - this.margin.bottom])
      .padding(0.1);
  }

  _initializeChart() {
    this.svg
      .attr('viewBox', `0 0 ${this.width} ${this.height}`)
      .attr('preserveAspectRatio', 'xMinYMin meet');

    this.xAxis = this.svg.append('g')
      .attr('transform', `translate(${this.margin.left},
          ${this.height - this.margin.bottom})`)
      .call(d3.axisBottom(this.xPosition));

    this.xLabel = this.svg.append('g')
      .append('text')
        .attr('transform', 
          `translate(${this.width / 2 - this.margin.left},${this.height})`)
        .text(this.units)
        .style('text-anchor', 'middle');
    
    this.yAxis = this.svg.append('g')
      .attr('transform', `translate(${this.margin.left},${this.margin.top})`)
      .attr('color', 'gray')
      .call(d3.axisLeft(this.yPosition).tickSize(0));
    
    this.bars = this.svg.append('g')
      .attr('transform', `translate(${this.margin.left},${this.margin.top})`);

    this.handleMouseOver = (d) => {
      this.tooltip
        .changeContent(`${d.key}: ${parseFloat(d.total).toFixed(2)}`);
      this.tooltip.show();

      d3.select(d3.event.target)
          .style('cursor', 'pointer')
        .transition()
        .duration(this.transitionTime / 5)
          .style("opacity", 1);
    };

    this.handleMouseLeave = () => {
      this.tooltip.hide();
      d3.select(d3.event.target)
        .transition()
        .duration(this.transitionTime / 5)
          .style("opacity", 0.6);
    };

    this.handleMouseMove = () => {
      this.tooltip.move(d3.event.pageX + 10, d3.event.pageY - 25);
    }

    this.handleClick = (d) => {
      console.log(d);
    }
  }

  setData(data) {
    this.data = data.sort((a, b) => d3.descending(a.total, b.total));
    this.color.domain(this.data.map(d => d.key).sort(d3.ascending));
  }

  drawChart() {
    this._drawXaxis();
    this._drawYaxis();
    this._drawBars();
  }

  _drawYaxis() {
    this.yPosition.domain(this.data.map(d => d.key));
    
    this.yAxis
      .call(d3.axisLeft(this.yPosition).tickSize(0));

    this.yAxis
      .selectAll('text')
      .data(this.data)
        .attr('transform', 
          `translate(${this.xPosition(0) + this.margin.left * 0.5},${0})`)
        .attr('opacity', 0.6)
        .attr('color', 'black')
      .transition()
      .duration(this.transitionTime)
        .attr('transform', d => {
          return `translate(
            ${this.xPosition(d.total) + this.margin.left * 0.8},${0})`;
        })
        .style('cursor', 'pointer')
        .style('text-anchor', 'start')
      .ease(d3.easeSinInOut);

    this.yAxis
      .selectAll('text')
        .on('mouseover', this.handleMouseOver)
        .on('mousemove', this.handleMouseMove)
        .on('mouseleave', this.handleMouseLeave)
        .on('click', this.handleClick);
  }

  _drawXaxis() {
    this.xPosition
      .domain(d3.extent([0, d3.max(this.data, d => d.total)]))
      .nice();

    this.xAxis
      .transition()
      .duration(this.transitionTime)
        .call(d3.axisBottom(this.xPosition));

    this.xAxis
      .selectAll('path, line')
        .attr('color', 'gray');

    this.xLabel
      .text(this.units);
  }

  _drawBars() {
    this.bars.selectAll('*').remove();

    this.bars
      .selectAll('path')
      .data(this.data)
      .enter()
      .append('path')
        .attr('d', d => this.rightRoundedRect(this.xPosition(0), 
          this.yPosition(d.key), this.xPosition(0), 
          this.yPosition.bandwidth(), 0))
        .attr('opacity', 0.6)
        .style('fill', d => this.color(d.key))
        .attr('pointer-events', 'none');

    this.bars
      .selectAll('path')
      .transition()
      .duration(this.transitionTime)
        .attr('d', d => {
          const r = this.xPosition(d.total) < 10 ? this.xPosition(d.total) : 10;
          return this.rightRoundedRect(this.xPosition(0), this.yPosition(d.key), 
            this.xPosition(d.total), this.yPosition.bandwidth(), r);
        })
      .ease(d3.easeSinInOut)
      .on('end', function () {
        d3.select(this)
          .attr('pointer-events', 'all');
      });

    this.bars
      .selectAll('path')
        .on('mouseover', this.handleMouseOver)
        .on('mousemove', this.handleMouseMove)
        .on('mouseleave', this.handleMouseLeave)
        .on('click', this.handleClick);
  }

  updateChart([minYear, maxYear]) {
    this.data.forEach(val => {
      val.total = d3.sum(val.value, d => 
        (d.year >= minYear && d.year <= maxYear) ?  d.value : 0)
    });

    this.data.sort((a, b) => d3.descending(a.total, b.total));

    this._drawXaxis();
    
    this.yPosition.domain(this.data.map(d => d.key));
    
    this.yAxis
      .transition()
      .duration(this.transitionTime)
      .call(d3.axisLeft(this.yPosition).tickSize(0));

    this.yAxis
      .selectAll('text')
      .data(this.data)
      .transition()
      .duration(this.transitionTime)
        .attr('transform', d => {
          return `translate(
            ${this.xPosition(d.total) + this.margin.left * 0.8},${0})`;
        })
      .ease(d3.easeSinInOut);

    this.bars
      .selectAll('path')
      .data(this.data)
        .attr('pointer-events', 'none')
        .style('fill', d => this.color(d.key))
      .transition()
      .duration(this.transitionTime)
        .attr('d', d => {
          const r = this.xPosition(d.total) < 10 ? this.xPosition(d.total) : 10;
          return this.rightRoundedRect(this.xPosition(0), this.yPosition(d.key), 
            this.xPosition(d.total), this.yPosition.bandwidth(), r);
        })
      .ease(d3.easeSinInOut)
      .on('end', function () {
        d3.select(this)
          .attr('pointer-events', 'all');
      });
  }

  setOnBarMouseMove(onMouseMove) {
    this.handleMouseMove = onMouseMove;
  }

  setOnBarMouseOver(onMouseOver) {
    this.handleMouseOver = onMouseOver;
  }

  setOnBarMouseLeave(onMouseLeave) {
    this.handleMouseLeave = onMouseLeave;
  }

  setOnClick(onClick) {
    this.handleClick = onClick;
  }

  rightRoundedRect(x, y, width, height, radius) {
    return "M" + x + "," + y
      + "h" + (width - radius)
      + "a" + radius + "," + radius + " 0 0 1 " + radius + "," + radius
      + "v" + (height - 2 * radius)
      + "a" + radius + "," + radius + " 0 0 1 " + -radius + "," + radius
      + "h" + (radius - width)
      + "z";
  }
}
